
import React, {
  useEffect,
  useState,
} from "react";

import {
  Button,
  Spinner,
} from "reactstrap";

import axios from "axios";

import dayjs from "dayjs";

import "./InvoicePage.css";


const InvoicePage = ({
  bookingId,
}) => {

  // =====================================================
  // STATE
  // =====================================================

  const [invoice, setInvoice] =
    useState(null);

  const [propertyProfile, setPropertyProfile] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");


  // =====================================================
  // IMAGE BASE URL
  // =====================================================

  const IMAGE_BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "";


  // =====================================================
  // IMAGE URL HELPER
  // =====================================================

  const getImageUrl = (imagePath) => {

    if (!imagePath) {
      return "";
    }

    // Already a complete URL
    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    return `${IMAGE_BASE_URL}${imagePath}`;
  };


  // =====================================================
  // LOAD INVOICE + PROPERTY PROFILE
  // =====================================================

  useEffect(() => {

    if (!bookingId) {
      return;
    }

    const loadData = async () => {

      try {

        setLoading(true);
        setError("");


        // =================================================
        // LOAD INVOICE
        // =================================================

        const invoiceResponse =
          await axios.get(
            `/api/invoices/${bookingId}`
          );


        if (
          invoiceResponse.data?.success
        ) {

          setInvoice(
            invoiceResponse.data.invoice
          );

        } else {

          setError(
            "Unable to load invoice"
          );

          return;
        }


        // =================================================
        // LOAD PROPERTY PROFILE
        // =================================================

        const profileResponse =
          await axios.get(
            "/api/property-profile"
          );


        console.log(
          "PROPERTY PROFILE FOR INVOICE:",
          profileResponse.data
        );


        if (
          profileResponse.data?.success
        ) {

          setPropertyProfile(
            profileResponse.data
          );

        }


      } catch (err) {

        console.error(
          "Invoice / Property profile loading error:",
          err
        );

        setError(
          err.response?.data?.message ||
          "Unable to load invoice"
        );

      } finally {

        setLoading(false);

      }

    };


    loadData();

  }, [bookingId]);


  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (

      <div className="invoice-loading">

        <Spinner />

        <span>
          Loading invoice...
        </span>

      </div>

    );

  }


  // =====================================================
  // ERROR
  // =====================================================

  if (error) {

    return (

      <div className="invoice-error">
        {error}
      </div>

    );

  }


  if (!invoice) {
    return null;
  }


  // =====================================================
  // PROPERTY DATA
  // =====================================================

  const profile =
    propertyProfile?.profile || {};

  const client =
    propertyProfile?.client || {};


  // =====================================================
  // HOTEL LOGO
  // =====================================================

  const hotelLogo =
    profile.hotelLogo ||
    client.hotelLogo ||
    "";


  // =====================================================
  // AUTHORIZED SIGNATURE
  // =====================================================

  const authorizedSignature =
    profile.authorizedSignature ||
    client.authorizedSignature ||
    "";


  // =====================================================
  // PROPERTY NAME
  // =====================================================

  const propertyName =
    client.propertyName ||
    invoice.billFrom?.name ||
    "Hotel";


  // =====================================================
  // SIGNATURE NAME
  // =====================================================
  //
  // Priority:
  // 1. Property name
  // 2. Contact person
  // 3. Admin name
  // 4. Invoice billFrom name
  //

  const signatureName =
    client.propertyName ||
    client.contactPerson ||
    client.adminName ||
    invoice.billFrom?.name ||
    "Authorized Signatory";


  // =====================================================
  // HELPERS
  // =====================================================

  const money = (value) =>
    `₹${Number(
      value || 0
    ).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;


  const formatDate = (
    date
  ) =>
    date
      ? dayjs(date).format(
          "DD MMM YYYY"
        )
      : "-";


  // =====================================================
  // PRINT
  // =====================================================

  const handlePrint = () => {
    window.print();
  };


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <>

      {/* ============================================= */}
      {/* PRINT BUTTON */}
      {/* ============================================= */}

      <div className="invoice-actions">

        <Button
          color="primary"
          onClick={handlePrint}
        >

          <i className="ri-printer-line me-1" />

          Print Invoice

        </Button>

      </div>


      {/* ============================================= */}
      {/* INVOICE */}
      {/* ============================================= */}

      <div className="invoice-page">


        {/* TOP BORDER */}

        <div className="invoice-top-border">

          <div className="invoice-corner invoice-corner-left" />

        </div>


        {/* ========================================= */}
        {/* HEADER */}
        {/* ========================================= */}

        <div className="invoice-header">


          <div className="invoice-title">
            INVOICE
          </div>


          <div className="invoice-property">


            {/* ===================================== */}
            {/* HOTEL LOGO */}
            {/* ===================================== */}

            <div className="property-logo">

              {hotelLogo ? (

                <img
                  src={getImageUrl(hotelLogo)}
                  alt={`${propertyName} Logo`}
                  className="invoice-hotel-logo"
                  onError={(e) => {

                    console.error(
                      "Hotel logo failed to load:",
                      e.currentTarget.src
                    );

                    e.currentTarget.style.display =
                      "none";

                  }}
                />

              ) : (

                <div className="property-logo-fallback">

                  {propertyName
                    ?.charAt(0)
                    ?.toUpperCase()}

                </div>

              )}

            </div>


            {/* ===================================== */}
            {/* PROPERTY NAME */}
            {/* ===================================== */}

            <div className="property-header-text">

              <div className="property-name">

                {propertyName}

              </div>


              <div className="property-label">

                HOTEL / HOSPITALITY

              </div>

            </div>


          </div>

        </div>


        {/* ========================================= */}
        {/* BILL TO / BILL FROM */}
        {/* ========================================= */}

        <div className="invoice-parties">


          {/* BILL TO */}

          <div className="party-box">

            <h3>
              Bill To
            </h3>


            <div className="party-content">


              <div>

                <strong>
                  Client Name:
                </strong>{" "}

                {
                  invoice.billTo
                    .name || "-"
                }

              </div>


              <div>

                <strong>
                  Email:
                </strong>{" "}

                {
                  invoice.billTo
                    .email || "-"
                }

              </div>


              <div>

                <strong>
                  Mobile:
                </strong>{" "}

                {
                  invoice.billTo
                    .mobile || "-"
                }

              </div>


              <div>

                <strong>
                  Address:
                </strong>{" "}

                {
                  invoice.billTo
                    .address || "-"
                }

              </div>


            </div>

          </div>


          {/* BILL FROM */}

          <div className="party-box bill-from">

            <h3>
              Bill From
            </h3>


            <div className="party-content">


              <div>

                <strong>
                  Property:
                </strong>{" "}

                {
                  invoice.billFrom
                    .name || "-"
                }

              </div>


              <div>

                <strong>
                  Email:
                </strong>{" "}

                {
                  invoice.billFrom
                    .email || "-"
                }

              </div>


              <div>

                <strong>
                  Mobile:
                </strong>{" "}

                {
                  invoice.billFrom
                    .mobile || "-"
                }

              </div>


              <div>

                <strong>
                  Address:
                </strong>{" "}

                {
                  invoice.billFrom
                    .address || "-"
                }

              </div>


              <div>

                <strong>
                  GST:
                </strong>{" "}

                {
                  invoice.billFrom
                    .gstNumber ||
                  "-"
                }

              </div>


              <div>

                <strong>
                  PAN:
                </strong>{" "}

                {
                  invoice.billFrom
                    .panNumber ||
                  "-"
                }

              </div>


            </div>

          </div>


        </div>


        {/* ========================================= */}
        {/* INVOICE META */}
        {/* ========================================= */}

        <div className="invoice-meta">

          <div>

            <strong>
              Invoice Number:
            </strong>{" "}

            {
              invoice.invoiceNumber
            }

          </div>


          <div>

            <strong>
              Invoice Date:
            </strong>{" "}

            {formatDate(
              invoice.invoiceDate
            )}

          </div>

        </div>


        {/* ========================================= */}
        {/* STAY DETAILS */}
        {/* ========================================= */}

        <section className="invoice-section">

          <h2>
            Stay Details:
          </h2>


          <table className="invoice-table">

            <thead>

              <tr>

                <th>
                  Room
                </th>

                <th>
                  Date of Arrival
                </th>

                <th>
                  Date of Departure
                </th>

                <th>
                  Nights
                </th>

                <th>
                  Room Rate / Night
                </th>

                <th>
                  Total
                </th>

              </tr>

            </thead>


            <tbody>

              <tr>

                <td>

                  {invoice.stay.roomNumber}

                  <small>

                    {
                      invoice.stay
                        .roomType
                    }

                  </small>

                </td>


                <td>

                  {formatDate(
                    invoice.stay.arrival
                  )}

                </td>


                <td>

                  {formatDate(
                    invoice.stay.departure
                  )}

                </td>


                <td>

                  {
                    invoice.stay
                      .nights
                  }

                </td>


                <td>

                  {money(
                    invoice.stay
                      .ratePerNight
                  )}

                </td>


                <td>

                  {money(
                    invoice.stay
                      .subtotal
                  )}

                </td>

              </tr>

            </tbody>

          </table>


          {/* STAY TOTALS */}

          <div className="invoice-total-area">


            <div>

              <span>
                Subtotal
              </span>

              <strong>

                {money(
                  invoice.stay
                    .subtotal
                )}

              </strong>

            </div>


            <div>

              <span>

                Tax (
                {
                  invoice.stay
                    .taxRate
                }
                %)

              </span>

              <strong>

                {money(
                  invoice.stay
                    .tax
                )}

              </strong>

            </div>


            <div className="grand-total-line">

              <span>
                Net Stay Charge
              </span>

              <strong>

                {money(
                  invoice.stay
                    .total
                )}

              </strong>

            </div>


          </div>

        </section>


        {/* ========================================= */}
        {/* SERVICE DETAILS */}
        {/* ========================================= */}

        <section className="invoice-section">

          <h2>
            Service Details:
          </h2>


          <table className="invoice-table service-table">

            <thead>

              <tr>

                <th>
                  No
                </th>

                <th>
                  Service
                </th>

                <th>
                  Item Name
                </th>

                <th>
                  Qty
                </th>

                <th>
                  Price / Item
                </th>

                <th>
                  Total
                </th>

              </tr>

            </thead>


            <tbody>

              {invoice.services
                ?.length ? (

                invoice.services.map(
                  (service, index) => (

                    <tr
                      key={`${service.service}-${index}`}
                    >

                      <td>
                        {index + 1}
                      </td>

                      <td>
                        {service.service}
                      </td>

                      <td>
                        {service.item}
                      </td>

                      <td>
                        {
                          service.quantity
                        }
                      </td>

                      <td>
                        {money(
                          service.price
                        )}
                      </td>

                      <td>
                        {money(
                          service.total
                        )}
                      </td>

                    </tr>

                  )
                )

              ) : (

                <tr>

                  <td
                    colSpan="6"
                    className="empty-services"
                  >

                    No additional
                    services

                  </td>

                </tr>

              )}

            </tbody>

          </table>


          {/* SERVICE TOTAL */}

          <div className="invoice-total-area">


            <div>

              <span>
                Subtotal
              </span>

              <strong>

                {money(
                  invoice
                    .serviceSummary
                    .subtotal
                )}

              </strong>

            </div>


            <div>

              <span>

                Tax (
                {
                  invoice
                    .serviceSummary
                    .taxRate
                }
                %)

              </span>

              <strong>

                {money(
                  invoice
                    .serviceSummary
                    .tax
                )}

              </strong>

            </div>


            <div className="grand-total-line">

              <span>
                Net Service Charge
              </span>

              <strong>

                {money(
                  invoice
                    .serviceSummary
                    .total
                )}

              </strong>

            </div>


          </div>

        </section>


        {/* ========================================= */}
        {/* PAYMENT INFORMATION */}
        {/* ========================================= */}

        <section className="payment-section">


          <div className="payment-title">

            Payment Information:

          </div>


          <div className="payment-body">


            <div className="payment-left">


              <div className="payment-row">

                <span>
                  Total Stay Charge
                </span>

                <strong>

                  {money(
                    invoice.payment
                      .stayCharge
                  )}

                </strong>

              </div>


              <div className="payment-row">

                <span>
                  Total Service Charge
                </span>

                <strong>

                  {money(
                    invoice.payment
                      .serviceCharge
                  )}

                </strong>

              </div>


              <div className="payment-row payment-final">

                <span>
                  Final Invoice Amount
                </span>

                <strong>

                  {money(
                    invoice.payment
                      .finalAmount
                  )}

                </strong>

              </div>


              <div className="payment-row">

                <span>
                  Paid Amount
                </span>

                <strong>

                  {money(
                    invoice.payment
                      .paidAmount
                  )}

                </strong>

              </div>


              <div className="payment-row balance-row">

                <span>
                  Balance Due
                </span>

                <strong>

                  {money(
                    invoice.payment
                      .balanceDue
                  )}

                </strong>

              </div>


            </div>


            <div className="payment-right">


              <div className="payment-method">

                <strong>
                  Payment Type
                </strong>

                <span>

                  {
                    invoice.payment
                      .paymentType
                  }

                </span>

              </div>


              <div className="invoice-date-right">

                <strong>
                  Date:
                </strong>{" "}

                {formatDate(
                  invoice.invoiceDate
                )}

              </div>


            </div>


          </div>

        </section>


        {/* ========================================= */}
        {/* FOOTER */}
        {/* ========================================= */}

        <div className="invoice-footer">


          {/* ======================================= */}
          {/* FOOTER LEFT */}
          {/* ======================================= */}

          <div className="footer-left">


            <h3>
              Questions
            </h3>


            <div>

              <strong>
                Email Us:
              </strong>{" "}

              {
                invoice.footer
                  .email || "-"
              }

            </div>


            <div>

              <strong>
                Call Us:
              </strong>{" "}

              {
                invoice.footer
                  .mobile || "-"
              }

            </div>


          </div>


          {/* ======================================= */}
          {/* FOOTER RIGHT */}
          {/* ======================================= */}

          <div className="footer-right">


            {/* SIGNATURE IMAGE */}

            {authorizedSignature ? (

              <img
                src={getImageUrl(
                  authorizedSignature
                )}
                alt="Authorized Signature"
                className="invoice-signature"
                onError={(e) => {

                  console.error(
                    "Signature failed to load:",
                    e.currentTarget.src
                  );

                  e.currentTarget.style.display =
                    "none";

                }}
              />

            ) : (

              <div className="signature-line" />

            )}


            {/* SIGNATURE NAME */}

            <div className="guest-signature">

              {signatureName}

            </div>


          </div>


        </div>


        {/* ========================================= */}
        {/* BOTTOM BORDER */}
        {/* ========================================= */}

        <div className="invoice-bottom-border">

          <div className="invoice-corner invoice-corner-right" />

        </div>


      </div>

    </>

  );

};


export default InvoicePage;